import { type ReactNode } from 'react'
import styles from './SegmentedControl.module.css'

export type SegmentedControlItem<TValue extends string> = {
  count?: ReactNode
  disabled?: boolean
  icon?: ReactNode
  label: string
  value: TValue
}

type SegmentedControlProps<TValue extends string> = {
  ariaLabel: string
  className?: string
  items: Array<SegmentedControlItem<TValue>>
  layout?: 'inline' | 'equal' | 'responsive'
  onChange: (value: TValue) => void
  value: TValue
  variant?: 'default' | 'panel'
}

export const SegmentedControl = <TValue extends string>({
  ariaLabel,
  className,
  items,
  layout = 'inline',
  onChange,
  value,
  variant = 'default'
}: SegmentedControlProps<TValue>) => (
  <div
    aria-label={ariaLabel}
    className={[
      styles.control,
      variant === 'panel' ? styles.panel : '',
      layout === 'equal' ? styles.equal : '',
      layout === 'responsive' ? styles.responsive : '',
      className
    ].filter(Boolean).join(' ')}
    role="group"
  >
    {items.map((item) => {
      const active = item.value === value

      return (
        <button
          aria-pressed={active}
          className={[styles.item, active ? styles.active : ''].filter(Boolean).join(' ')}
          disabled={item.disabled}
          key={item.value}
          onClick={() => onChange(item.value)}
          type="button"
        >
          {item.icon}
          <span>{item.label}</span>
          {item.count !== undefined && <strong className={styles.count}>{item.count}</strong>}
        </button>
      )
    })}
  </div>
)
